import React, { ReactElement, useMemo } from 'react';
import { PricesItemContainer } from '../pricesItem';
import { PricesCustomerStyled, WrapperStyled } from './pricesCustomer.styled';
import { PricesCustomerContainerProps } from './pricesCustomer.model';
import {
  CommonEventTypes,
  getCurrencies,
  getVendors,
  textGradient,
  useTheme,
  useTranslations,
} from '@lerna-core';
import { CustomerPriceInfoContainer } from './customerPriceInfo';
import {
  OrderFormEvents,
  orderFormPushDataLayer,
} from '@features/common/forms';
import { useCompensationFormContext } from '../../../../compensationForm.context';
import { getCustomerPrices } from '@features/common/course';
import { useSelector } from 'react-redux';

export const PricesCustomerContainer = ({
  withInstallment = true,
}: PricesCustomerContainerProps): ReactElement => {
  const theme = useTheme();
  const translations = useTranslations();
  const currencies = useSelector(getCurrencies);
  const vendors = useSelector(getVendors);
  const { course, isLernaPrime, eventPosition, isOnlinePaySelected } =
    useCompensationFormContext();
  const {
    startPrice,
    hasCorporateDiscount,
    corporateDiscountValue,
    corporateDiscountAmount,
    hasBonusPointsDiscount,
    bonusPointsDiscount,
    hasCompanyCompensation,
    companyCompensation,
    totalPrice,
    hasOneOfDiscounts,
    hasInstallment,
  } = useMemo(() => getCustomerPrices(course, currencies), []);

  const showStartPrice =
    (withInstallment && hasInstallment) || hasOneOfDiscounts;
  const showTotalPrice =
    withInstallment &&
    hasInstallment &&
    hasOneOfDiscounts &&
    !course.isFreeByDiscount;
  const showBonusPoints = hasBonusPointsDiscount && !course.isFreeByDiscount;

  const corporateDiscountTitle = `${translations?.customer_discount_label} - ${corporateDiscountValue}`;

  const gradientText = textGradient(
    theme.COLOR.DEFAULT_GRADIENT_START,
    theme.COLOR.DEFAULT_GRADIENT_END
  );

  const bonusPointsTitle = isLernaPrime
    ? translations?.company_discount_bonuses_label
    : translations?.discount_bonuses_label;

  const handleCompensationTooltipClick = (): void => {
    orderFormPushDataLayer(
      OrderFormEvents.formEnrollCourseOpenPromptAboutMotivation,
      CommonEventTypes.coursesEvents,
      eventPosition,
      course,
      vendors
    );
  };

  return (
    <PricesCustomerStyled>
      {!course.isFree && (
        <WrapperStyled>
          {showStartPrice && (
            <PricesItemContainer
              title={translations?.item_price_label}
              value={startPrice}
              itemStyles={
                hasOneOfDiscounts
                  ? {
                      value: `text-decoration: line-through; color: ${theme.COLOR.TEXT_LIGHT_COLOR}`,
                    }
                  : undefined
              }
            />
          )}
          {hasCorporateDiscount && (
            <PricesItemContainer
              title={corporateDiscountTitle}
              value={corporateDiscountAmount}
              isNegative
            />
          )}
          {showBonusPoints && (
            <PricesItemContainer
              title={bonusPointsTitle}
              value={bonusPointsDiscount}
              isNegative
              itemStyles={{
                title: gradientText,
                value: gradientText,
              }}
            />
          )}
          {hasCompanyCompensation && (
            <PricesItemContainer
              title={translations?.b2b_compensation_price_title}
              value={companyCompensation}
              isNegative
              tooltipText={translations?.b2b_compensation_price_tooltip_text}
              handleTooltipClick={handleCompensationTooltipClick}
            />
          )}
          {showTotalPrice && (
            <PricesItemContainer
              title={translations?.total_price_label}
              value={totalPrice}
              itemStyles={{
                item: 'font-weight: 700;',
              }}
            />
          )}
        </WrapperStyled>
      )}
      <CustomerPriceInfoContainer
        course={course}
        withInstallment={withInstallment}
        withOnlinePayment={isOnlinePaySelected}
      />
    </PricesCustomerStyled>
  );
};
